/**
 * GateTecnologiaScreen — Quality Gate 2 · Escopo de Tecnologia (gate de FASE).
 *
 * Porta o Gate de Tecnologia do protótipo: as personas técnicas (Arquiteto de
 * Soluções · Tech Lead · Arq. Infraestrutura · Arq. Segurança) validam o BLUEPRINT
 * (stack decidida), os ARTEFATOS técnicos e os COMPONENTES de reuso — tudo o que já
 * está na memória da Fase 3 — com parecer, SLA e auditoria hash-chain. Aprova/Rejeita
 * via PATCH /projects/{id}/journey-gates/tecnologia (auditável). Lê ?project=<id>.
 */
import { C } from '@/theme/palette';
import { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import {
  useJourneyGate, useDecideJourneyGate,
  useTechBlueprint, useArtifacts, useComponents,
} from '@/api/techScope';
import { GateExportBar } from './GateExportBar';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

// Aprovadores/personas/SLA do Gate de Tecnologia (espelha o protótipo · GATE_DEFS.tecnologia).
const GATE_DEF = {
  num: 2, label: 'Escopo de Tecnologia', sla: '24h',
  personas: 'Arquiteto de Soluções · Tech Lead · Arq. Infraestrutura · Arq. Segurança',
  approvers: [
    { name: 'Carlos Oliveira', role: 'Arquiteto de Soluções', avatar: 'CO' },
    { name: 'Gabriel Chante', role: 'Tech Lead', avatar: 'GC' },
    { name: 'Renata Alves', role: 'Arquiteta de Infraestrutura', avatar: 'RA' },
    { name: 'Paulo Menezes', role: 'Arquiteto de Segurança', avatar: 'PM' },
  ],
};

const ST = {
  approved: { label: 'APROVADO', fg: C.ok, bg: 'rgba(1,230,228,0.12)' },
  rejected: { label: 'REJEITADO', fg: C.bad, bg: 'rgba(255,51,0,0.12)' },
  pending:  { label: 'AGUARDANDO APROVAÇÃO', fg: C.warn, bg: 'rgba(255,176,32,0.12)' },
};

function Card({ title, right, accent = C.accent, children }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
          letterSpacing: '0.1em', color: accent }}>{title}</h4>
        {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 14px',
      border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface }}>
      <span style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{value}</span>
    </div>
  );
}

function Tags({ items, color = C.accent }) {
  const arr = (items ?? []).filter(Boolean);
  if (!arr.length) return <span style={{ fontSize: 12, color: C.muted }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {arr.map((t, i) => (
        <span key={i} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 6,
          background: `${color}14`, color, border: `1px solid ${color}44` }}>{String(t)}</span>
      ))}
    </div>
  );
}

export function GateTecnologiaScreen({ projectId: propProjectId, onContinue, onBack } = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = propProjectId || urlProject || projects?.[0]?.id || null;

  const { data: gate } = useJourneyGate(projectId, 'tecnologia');
  const decide = useDecideJourneyGate(projectId, 'tecnologia');
  const { data: blueprint } = useTechBlueprint(projectId);
  const { data: artifactsData } = useArtifacts(projectId);
  const { data: componentsData } = useComponents(projectId);
  const [comment, setComment] = useState('');

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  const status = gate?.status ?? 'pending';
  const s = ST[status] || ST.pending;
  const approved = status === 'approved';

  const languages = blueprint?.languages ?? [];
  const frameworks = blueprint?.frameworks ?? [];
  const infrastructure = blueprint?.infrastructure ?? [];
  const databases = blueprint?.databases ?? [];
  // GET /tech-blueprint responde snake_case (target_architecture); POST /generate
  // responde camelCase (targetArchitecture). Aceita os dois para não sumir a
  // arquitetura-alvo no gate.
  const architecture = blueprint?.target_architecture ?? blueprint?.targetArchitecture ?? '';
  const hasBlueprint = Boolean(architecture || languages.length || frameworks.length);

  const artifacts = artifactsData?.artifacts ?? [];
  const components = componentsData?.components ?? [];
  const reused = components.filter((c) => c.selected);
  const tokensSaved = reused.reduce((a, c) => a + (c.tokensSaved || 0), 0);

  const goBlueprint = () => navigate(`/journey/blueprint?project=${projectId}`);
  // Embutido no wizard: avança para a próxima etapa (Backlog) via onContinue.
  // Standalone (/gates/tecnologia): volta para a Gestão do Projeto.
  const goJourney = () => (onContinue ? onContinue() : navigate(`/project?project=${projectId}`));

  // ── objeto normalizado para exportação (.md / PDF) — GateExportBar ─────────
  const exportGate = {
    num: GATE_DEF.num,
    label: GATE_DEF.label,
    projectId,
    description: 'Encerramento do Escopo de Tecnologia — as personas técnicas validam o '
      + 'Blueprint (stack decidida), os artefatos técnicos e os componentes de reuso antes '
      + 'de liberar os Ciclos.',
    status,
    statusLabel: s.label,
    decidedAt: gate?.decidedAt,
    comment: gate?.comment,
    sla: GATE_DEF.sla,
    personas: GATE_DEF.personas,
    approvers: GATE_DEF.approvers.map((a) => ({ ...a, done: approved })),
    sections: [
      hasBlueprint ? {
        title: 'Blueprint técnico — stack decidida',
        kind: 'kv',
        data: [
          ['Linguagens', languages.join(', ')],
          ['Frameworks', frameworks.join(', ')],
          ['Infraestrutura', infrastructure.join(', ')],
          ['Bancos de dados', databases.join(', ')],
          ['Arquitetura-alvo', architecture],
        ],
      } : null,
      artifacts.length ? {
        title: 'Artefatos técnicos gerados',
        kind: 'list',
        items: artifacts.map((a) => a.title),
      } : null,
      reused.length ? {
        title: `Componentes de reuso confirmados (economia −${tokensSaved.toLocaleString('pt-BR')} tokens)`,
        kind: 'list',
        items: reused.map((c) => `**${c.name}**${c.origin ? ` — origem: ${c.origin} · usado em ${c.usedIn || 1} projeto(s)` : ''}`),
      } : null,
    ].filter(Boolean),
  };

  return (
    <div className="sp-page" style={{ color: C.text, maxWidth: 980 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>
          🛡 Quality Gate {GATE_DEF.num} — {GATE_DEF.label}
        </h2>
        <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
          color: s.fg, background: s.bg, letterSpacing: '0.04em' }}>{s.label}</span>
        <GateExportBar gate={exportGate} style={{ marginLeft: 'auto' }} />
      </div>
      <p style={{ color: C.dim, marginTop: 8, fontSize: 13.5, maxWidth: '80ch' }}>
        Encerramento do Escopo de Tecnologia — as personas técnicas validam o Blueprint (stack
        decidida), os artefatos técnicos e os componentes de reuso antes de liberar os Ciclos.
        Tudo aqui já está na memória e alimenta 100% a execução.
      </p>

      {/* Pills de métricas */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
        <Pill label="Blueprint" value={hasBlueprint ? 'Definido ✓' : '—'} />
        <Pill label="Artefatos técnicos" value={String(artifacts.length || '—')} />
        <Pill label="Componentes reusados" value={String(reused.length || '—')} />
        {tokensSaved > 0 && <Pill label="Economia por reuso" value={`−${tokensSaved.toLocaleString('pt-BR')} tk`} />}
      </div>

      {gate?.decidedAt && (
        <p style={{ color: C.muted, fontSize: 12.5, marginTop: 12 }}>
          Decidido em {new Date(gate.decidedAt).toLocaleString('pt-BR')}
          {gate.comment ? ` · ${gate.comment}` : ''}
        </p>
      )}
      {decide.isError && (
        <div style={{ marginTop: 12, color: C.bad, fontSize: 13 }}>
          {String(decide.error?.title || decide.error?.message || decide.error)}
        </div>
      )}

      {/* Blueprint — stack decidida */}
      <Card title="Blueprint técnico — stack decidida"
        right={<button onClick={goBlueprint} style={{ cursor: 'pointer', background: 'transparent',
          color: C.accent, border: `1px solid ${C.accent}55`, borderRadius: 7, padding: '5px 11px',
          fontSize: 12 }}>👁 Abrir no Blueprint</button>}>
        {hasBlueprint ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {[['Linguagens', languages], ['Frameworks', frameworks],
              ['Infraestrutura', infrastructure], ['Bancos de dados', databases]].map(([t, v]) => (
              <div key={t} style={{ padding: 12, background: C.black, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: C.muted,
                  textTransform: 'uppercase', marginBottom: 8 }}>{t}</div>
                <Tags items={v} />
              </div>
            ))}
            {architecture && (
              <div style={{ gridColumn: '1 / -1', padding: 12, background: C.black, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: C.muted,
                  textTransform: 'uppercase', marginBottom: 6 }}>Arquitetura-alvo</div>
                <div style={{ fontSize: 12.5, color: C.dim, whiteSpace: 'pre-wrap', lineHeight: 1.5,
                  maxHeight: 160, overflow: 'auto' }}>{architecture}</div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            Blueprint ainda não gerado — volte à etapa Blueprint antes de aprovar o gate.
          </div>
        )}
      </Card>

      {/* Artefatos técnicos gerados */}
      <Card title="Artefatos técnicos gerados" accent={C.quantum}>
        {artifacts.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
            {artifacts.map((a) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                background: C.black, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <span style={{ color: C.quantum }}>✓</span>
                <span style={{ fontSize: 12.5, color: C.dim }}>{a.title}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            Nenhum artefato gerado ainda — gere na etapa Artefatos para dar rastreabilidade ao gate.
          </div>
        )}
      </Card>

      {/* Componentes de reuso confirmados */}
      {reused.length > 0 && (
        <Card title="Componentes de reuso confirmados" accent={C.digital}
          right={<span style={{ fontSize: 10, fontWeight: 700, color: C.digital,
            border: `1px solid ${C.digital}66`, borderRadius: 5, padding: '2px 7px' }}>ECONOMIA −{tokensSaved.toLocaleString('pt-BR')} TK</span>}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
            {reused.map((c) => (
              <div key={c.id} style={{ padding: 12, background: C.black, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>♻ {c.name}</div>
                {c.origin && <div style={{ fontSize: 11, color: C.muted }}>origem: {c.origin} · usado em {c.usedIn || 1} projeto(s)</div>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Painel de aprovadores + SLA + auditoria + parecer */}
      <div style={{ marginTop: 20, border: `1px solid ${C.border}`, background: C.surface2, borderRadius: 12, padding: 16 }}>
        {approved && (
          <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8,
            background: 'rgba(1,230,228,0.06)', border: `1px solid ${C.accent}44`, fontSize: 12.5, color: C.dim }}>
            <strong style={{ color: C.accent }}>Gate aprovado — Escopo de Tecnologia concluído.</strong> Aprovações
            registradas na trilha de auditoria imutável (hash-chain).
          </div>
        )}
        <div style={{ fontSize: 10, letterSpacing: '0.05em', color: C.muted, fontWeight: 700, marginBottom: 8 }}>
          APROVADORES · {GATE_DEF.personas.toUpperCase()}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          {GATE_DEF.approvers.map((a) => (
            <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 8,
              border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 12px 4px 4px', background: C.black }}>
              <span style={{ width: 26, height: 26, borderRadius: 999, background: approved ? C.accent : C.surface2,
                color: approved ? C.black : C.dim, display: 'grid', placeItems: 'center',
                fontSize: 10, fontWeight: 700 }}>{a.avatar}</span>
              <span style={{ fontSize: 12 }}>
                <span style={{ color: C.text, fontWeight: 600 }}>{a.name}</span>
                <span style={{ color: C.muted }}> · {a.role}</span>
              </span>
              {approved && <span style={{ color: C.ok, fontSize: 12 }}>✓</span>}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          ⏱ SLA: aprovação em até {GATE_DEF.sla} · Escala para backup em 48h · Admin em 72h
        </div>

        <label style={{ fontSize: 12.5, color: C.dim }}>Parecer do aprovador (obrigatório para rejeitar)</label>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
          placeholder="Ex: a infraestrutura precisa de rede/observabilidade definidas antes do Ciclo 0…"
          style={{ width: '100%', marginTop: 6, background: C.black, color: C.text,
            border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {onBack && (
            <button onClick={onBack} style={{ background: 'transparent', color: C.dim,
              border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 16px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer' }}>
              ← Voltar
            </button>
          )}
          {approved ? (
            <button onClick={goJourney} style={{ background: C.accent, color: C.black, border: 'none',
              borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Continuar jornada →
            </button>
          ) : (
            <>
              <button onClick={() => decide.mutate({ decision: 'approve', comment })} disabled={decide.isPending}
                style={{ background: C.accent, color: C.black, border: 'none', borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: decide.isPending ? 0.6 : 1 }}>
                {decide.isPending ? 'Registrando…' : 'Aprovar Escopo de Tecnologia'}
              </button>
              <button onClick={() => decide.mutate({ decision: 'reject', comment })} disabled={decide.isPending}
                style={{ background: 'transparent', color: C.bad, border: `1px solid ${C.bad}`, borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Rejeitar</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
